import { activateApplication } from 'core/api/applications';
import { updateOneCacheItem } from 'core/cache/helpers';
import { Application, UserData } from 'types';
import ButtonLoader from 'ui/components/button-loader/ButtonLoader';
import ErrorsList from 'ui/components/errors-list/ErrorsList';
import StatusCard from 'ui/components/status-card/StatusCard';
import { isAdmin, isClient } from 'ui/helpers/users';

import { Button } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';

export interface InactiveApplicationStateProps {
  applicationId: string;
  currentUser: UserData;
}

export default function InactiveApplicationState(props: InactiveApplicationStateProps) {
  const { applicationId, currentUser } = props;

  const isViewable = isClient(currentUser.userProfile.roles);
  const isUserAdmin = isAdmin(currentUser.userProfile.roles);

  const queryKey = ['applications', applicationId];
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (id: string) => activateApplication(id),
    onSuccess: (application: Application) => {
      updateOneCacheItem<Application>(queryClient, queryKey, application);
    },
  });

  const handleActivate = () => {
    mutation.mutate(applicationId);
  };

  if (!(isViewable || isUserAdmin)) {
    return null;
  }

  return (
    <>
      <StatusCard
        title="Inactive Application"
        content="This application is currently inactive. It was deactivated by the system due to inactivity for more than 180 days."
        isLoading={false}
        status="failed"
        button={
          isUserAdmin && (
            <Button
              type="button"
              variant="contained"
              color="primary"
              disabled={mutation.isPending}
              disableElevation
              onClick={handleActivate}
            >
              <ButtonLoader isActive={mutation.isPending} />
              Activate
            </Button>
          ) || null
        }
      />
      {mutation.isError && <ErrorsList title="Unexpected Error" errors={mutation.error as string[]} />}
    </>
  );
}
